import React, { useState, useEffect } from 'react';
import { parsePhoneNumberFromString, getCountries, getCountryCallingCode } from 'libphonenumber-js';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import Swal from 'sweetalert2';
import NewBirthForm from './NewBirthForm';
import Logo from '../logo/Rccglogo.png';

const VisitorExtra = () => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const { isDark } = useTheme();
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({
    title: '',
    customTitle: '',
    surname: '',
    firstName: '',
    phone: '',
    country: 'NG', // Default to Nigeria
    email: '',
    howHeard: '',
    ageGroup: '',
    occupation: '',
    location_id: '',
    other_city: '',
    dobMonth: '',
    dobDay: '',
    dateOfBirth: '',
    firstTime: 'Yes',
    status: 'new',
    prayerRequests: '',
    feedback: '',
    considerAttending: '',
    favoriteParts: [],
    residencyStatus: '',
    firstVisitDate: new Date().toISOString().slice(0, 10),
    receivedFollowUp: '',
    baptized: '',
    wouldLikeBaptism: '',
    workforce: '',
    department: '',
    previousParish: '',
    preferredVisitTime: '',
    preferredVisitDay: '',
    visitNotes: '',
    maritalStatus: '',
    gender: '',
    // numberOfPeople: 1,
    serviceAttended: 'first_service',
  });
  const [visitType, setVisitType] = useState('first');
  const [visitLocked, setVisitLocked] = useState(false);
  const [showOtherCity, setShowOtherCity] = useState(false);

  // Load locations on component mount
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const { data } = await axios.get(`${deriveApiBase()}/locations`, { params: { active_only: true } });
        if (data.success) {
          setLocations(data.data);
        }
      } catch (error) {
        console.error('Error loading locations:', error);
      }
    };
    loadLocations();
  }, []);

  // Function to format Nigerian phone numbers to international format without +
  const getCountryDialCode = country => {
    try {
      return getCountryCallingCode(country || form.country);
    } catch {
      return '';
    }
  };

  const formatPhoneNumber = (phone, country) => {
    if (!phone) return '';
    // Format to international format for any selected country
    const phoneNumber = parsePhoneNumberFromString(phone, country || form.country);
    return phoneNumber ? phoneNumber.number.replace('+', '') : phone.replace(/\D/g, '');
  };

  // Function to validate phone number format
  const isValidPhoneNumber = (phone, country) => {
    const phoneNumber = parsePhoneNumberFromString(phone, country || form.country);
    return phoneNumber ? phoneNumber.isValid() : false;
  };

  const handleCountryChange = e => {
    const newCountry = e.target.value;
    const dialCode = getCountryDialCode(newCountry);
    let nextPhone = form.phone || '';

    if (dialCode && !nextPhone.trim()) {
      nextPhone = `+${dialCode}`;
    }

    setForm({ ...form, country: newCountry, phone: nextPhone });
  };

  // Handle phone number input
  const handlePhoneChange = e => {
    const input = e.target.value;
    setForm({ ...form, phone: input });
  };

  // Format phone number when user leaves the field
  const handlePhoneBlur = e => {
    const formatted = formatPhoneNumber(e.target.value, form.country);
    if (formatted && isValidPhoneNumber(formatted, form.country)) {
      setForm({ ...form, phone: formatted });
    }
  };

  // Dynamic banner styles based on selection
  const bannerStyles = () => {
    if (visitType === 'first') {
      return { bg: 'bg-green-600', text: 'text-green-800', title: 'FIRST TIMERS FORM' };
    }
    if (visitType === 'newbirth') {
      return { bg: 'bg-indigo-900', text: 'text-indigo-900', title: 'NEW BIRTH FORM' };
    }
    return { bg: 'bg-amber-900', text: 'text-amber-900', title: 'SECOND TIMERS FORM' };
  };

  // API Base URL - prefer device host for mobile access
  const deriveApiBase = () => {
    // Force production URL when deployed
    if (window.location.hostname === 'rccgtr.net' || window.location.hostname.includes('rccgtr')) {
      return 'https://rccgtr.net/api';
    }
    
    // Development only
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    }
    
    // Fallback to relative path
    return '/api';
  };
  const API_BASE_URL = deriveApiBase();

  const handleSubmit = async e => {
    e.preventDefault();

     // Extra validation
  if (form.title === 'Other' && !form.customTitle.trim()) {
    Swal.fire({
      icon: 'warning',
      title: 'Missing Title',
      text: 'Please enter a custom title.',
    });
    return;
  }

  if (visitType === 'first' && form.favoriteParts.length < 3) {
   
    Swal.fire({
      icon: 'warning',
      title: 'favorite parts',
      text: 'Please select at least 3 favorite parts.!',
    });
    return;
  }

    // Format phone number before submission
    const formattedPhone = formatPhoneNumber(form.phone);

    // Validate phone number
    if (!isValidPhoneNumber(form.phone, form.country)) {
      setMessage(`Please enter valid Selected Country phone number (e.g., ${form.country === 'NG' ? '08012345678 or 8012345678' : 'e.g., 4567890345 in international format without the + sign'})`);
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Phone Number',
        text: 'Please enter a valid phone number for the selected country.',
      });
      return;
    }

    try {
      setLoading(true);

      // Construct DOB as YYYY-MM-DD but ignore year (use "1900" or "0001")
      const dateOfBirth =
        form.dobMonth && form.dobDay ? `1900-${form.dobMonth}-${form.dobDay}` : null;

      // Prepare data for backend
      const visitorData = {
        first_name: form.firstName || '',
        last_name: form.surname || '',
        phone: formattedPhone, // Use formatted phone number
        email: form.email || null,
        visitor_type: visitType === 'first' ? 'first_timer' : 'returning_member',
        service_attended: form.serviceAttended,
        address: form.location || null,
        location_id: form.location_id && form.location_id !== 'other' ? form.location_id : 11,
        age: form.ageGroup ? parseInt(form.ageGroup.split('-')[0]) : null,
        gender: form.gender || null,
        visit_date: form.firstVisitDate,
        service_type: 'sunday',
        notes: form.prayerRequests || form.feedback || null,
        source: form.howHeard || null,
        // 🎯 Set status based on button
        status: visitType === 'first' ? 'new' : 'return',
        date_of_birth: dateOfBirth || null,
        // First Timer specific fields
        // title: form.title || null,
        title: form.title === 'Other' ? form.customTitle : form.title || null,
        marital_status: form.maritalStatus || null,
        age_group: form.ageGroup || null,
        how_heard: form.howHeard || null,
        prayer_request: form.prayerRequests || null,
        feedback: form.feedback || null,
        consider_attending: form.considerAttending || null,
        favorite_parts: (form.favoriteParts || []).join(','),
        residency_status: form.residencyStatus || null,
        other_city: form.other_city || null,
        // Second Timer specific fields
        first_visit_date: form.firstVisitDate || null,
        received_follow_up: form.receivedFollowUp || null,
        baptized: form.baptized || null,
        would_like_baptism: form.wouldLikeBaptism || null,
        join_workforce: form.workforce || null,
        department: form.department || null,
        preferred_visit_time: form.preferredVisitTime || null,
        preferred_visit_day: form.preferredVisitDay || null,
        visit_notes: form.visitNotes || null,
        // RCCG specific fields
        previous_parish: form.previousParish || null,
        // Additional fields
        // number_of_people: form.numberOfPeople || 1,
      };

      const { data } = await axios.post(`${API_BASE_URL}/visitors`, visitorData, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (data.success) {
        setMessage('Guest added successfully!');
        Swal.fire({
          icon: 'success',
          title: 'Guest Added',
          text: 'Guest added successfully!',
        });
        // Reset form
        setForm({
          title: '',
          surname: '',
          firstName: '',
          phone: '',
          email: '',
          howHeard: '',
          ageGroup: '',
          occupation: '',
          location_id: '',
          other_city: '',
          dateOfBirth: '',
          firstTime: 'Yes',
          prayerRequests: '',
          feedback: '',
          considerAttending: '',
          favoriteParts: [],
          residencyStatus: '',
          firstVisitDate: new Date().toISOString().slice(0, 10),
          receivedFollowUp: '',
          baptized: '',
          wouldLikeBaptism: '',
          workforce: '',
          department: '',
          previousParish: '',
          preferredVisitTime: '',
          preferredVisitDay: '',
          visitNotes: '',
          maritalStatus: '',
          gender: '',
          // numberOfPeople: 1,
        });
      } else if (data.error && data.data) {
        // ✅ Handle duplicate visitor
        setMessage(
          `Already registered on ${new Date(data.data.visit_date).toLocaleDateString()}`
        );
        Swal.fire({
          icon: 'info',
          title: 'Duplicate Entry',
          html: `
            <p><strong>${data.data.first_name} ${data.data.last_name}</strong> 
            (Visitor Type: ${data.data.visitor_type})</p>
            <p>Already registered on <strong>${new Date(data.data.visit_date).toLocaleDateString()}</strong>.</p>
          `,
        });
      } else {
        setMessage(data.error || 'Failed to add guest');
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: data.error || 'Failed to add guest',
        });
      }
    } catch (error) {
      console.error('Error adding guest:', error);

      if (error.response && error.response.data) {
        // Show the error sent by backend (e.g., "Visitor already registered on ...")
        Swal.fire({
          icon: 'error',
          title: 'Connection Error',
          text: `${error.response.data.error || 'An error occurred'}`,
        });
        setMessage(error.response.data.error || 'An error occurred');
      } else {
        // Fallback for network or unexpected errors
        setMessage('Error adding guest. Please check your connection and try again.');
        Swal.fire({
          icon: 'error',
          title: 'Connection Error',
          text: 'Error adding guest. Please check your connection and try again.',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div
        className={`w-full max-w-4xl mb-2 ${
          isDark ? 'bg-gray-800 text-indigo-100' : 'bg-white text-gray-800'
        } rounded-2xl shadow p-6`}
      >
        {/* Dynamic Banner Header */}
        

        {/* Dynamic Banner Header */}
        <div className={`mb-6 p-6 rounded-2xl ${bannerStyles().bg}`}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-white rounded-xl  gap-4 m-4 p-6">
            {/* Left side: Logo + Church Info */}
            <div className="flex items-center gap-3">
              <img src={Logo} alt="RCCG Logo" className="w-10 h-10" />
              <div>
              <p className={`text-lg md:text-xl font-extrabold ${bannerStyles().text.replace('text-', 'text-')}`}>THE THRONE ROOM</p>
              <p className="text-sm text-gray-500">Transcorp Hilton, Abuja.</p>
              </div>
            </div>

            {/* Right side: Title */}
            <div className="text-center md:text-right">
              <p className="text-base md:text-lg font-extrabold text-gray-700">
                {bannerStyles().title}
              </p>
            </div>
          </div>
        </div>


        <hr className="mb-6" />
        <p calssname={`text-sm text-gray-500" ${
                  isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                }`}> Please be assured that your information collected on this form will be held in strict confidentiality by RCCG Throne Room and will not be shared with any third party.</p>

        <div className="flex flex-wrap gap-3 mb-4 justify-center">
          <button
            type="button"
            onClick={() => { setVisitType('first'); setVisitLocked(true); }}
            className={`${
              visitType === 'first' ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700'
            } px-4 py-2 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed`}
            disabled={visitLocked && visitType !== 'first'}
          >
            🆕 I'm a First Timer
          </button>
          <button
            type="button"
            onClick={() => { setVisitType('second'); setVisitLocked(true); }}
            className={`${
              visitType === 'second' ? 'bg-amber-900 text-white' : 'bg-indigo-50 text-amber-900'
            } px-4 py-2 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed`}
            disabled={visitLocked && visitType !== 'second'}
          >
            🔁 I've Been Here Before
          </button>
          <button
            type="button"
            onClick={() => { setVisitType('newbirth'); setVisitLocked(true); }}
            className={`${
              visitType === 'newbirth' ?  'bg-indigo-900 hover:bg-blue-700 text-white' : 'bg-indigo-50 text-indigo-700'
            } px-4 py-2 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed`}
            disabled={visitLocked && visitType !== 'newbirth'}
          >
            🙏 I Just Surrendered My Life
          </button>
        </div>

        {/* Message Display */}
        {message && (
          <div
            className={`mt-4 p-4 rounded-lg ${
              message.includes('successfully')
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {message}
            <button onClick={() => setMessage('')} className="float-right font-bold">
              ×
            </button>
          </div>
        )}

        {visitType !== 'newbirth' && (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Country selector for phone formatting */}
          <div>
            <label className="block text-sm mb-1">Country <span className="text-red-600">*</span></label>
            <select
              className={`w-full p-3 border rounded ${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''}`}
              name="country"
              value={form.country}
              onChange={handleCountryChange}
              required
            >
              {(() => {
                const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
                return getCountries().map((code) => (
                  <option key={code} value={code}>
                    {code === 'NG' ? `${displayNames.of(code)} (default)` : displayNames.of(code)}
                  </option>
                ));
              })()}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm mb-1">Title  <span className="text-red-600">*</span></label>
              <select
                className={`w-full p-3 border rounded ${
                  isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                }`}
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
              >
                <option value="">Select Title</option>
                <option value="Mr">Mr</option>
                <option value="Mrs">Mrs</option>
                <option value="Miss">Miss</option>
                <option value="Other">Others</option>
              </select>

              {form.title === 'Other' && (
              <input
                type="text"
                placeholder="Enter title"
                className={`w-full p-3 border rounded ${
                  isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                }`}
                value={form.customTitle}
                onChange={e => setForm({ ...form, customTitle: e.target.value })}
                required={form.title === 'Other'}
              />
              )}
            </div>
            <div>
              <label className="block text-sm mb-1">Surname <span className="text-red-600">*</span></label>
              <input
                className={`w-full p-3 border rounded ${
                  isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                }`}
                value={form.surname}
                onChange={e => setForm({ ...form, surname: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm mb-1">First Name <span className="text-red-600">*</span></label>
              <input
                className={`w-full p-3 border rounded ${
                  isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                }`}
                value={form.firstName}
                onChange={e => setForm({ ...form, firstName: e.target.value })}
                required
              />
            </div>
            
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
              <label className="block text-sm mb-1">Phone Number <span className="text-red-600">*</span></label>
              <input
                className={`w-full p-3 border rounded ${
                  isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                }`}
                value={form.phone}
                onChange={handlePhoneChange}
                onBlur={handlePhoneBlur}
                placeholder={`+${getCountryDialCode(form.country)}xxxxxxxxxx`}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Format: +{getCountryDialCode(form.country)}XXXXXXXXXX
              </p>
            </div>
            <div>
              <label className="block text-sm mb-1">Email Address</label>
              <input
                type="email"
                className={`w-full p-3 border rounded ${
                  isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                }`}
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
        
              />
            </div>
            
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
              <label className="block text-sm mb-1">Gender <span className="text-red-600">*</span></label>
              <select
                className={`w-full p-3 border rounded ${
                  isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                }`}
                value={form.gender}
                onChange={e => setForm({ ...form, gender: e.target.value })}
                requireds
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1">Marital Status <span className="text-red-600">*</span></label>
              <select
                className={`w-full p-3 border rounded ${
                  isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                }`}
                value={form.maritalStatus}
                onChange={e => setForm({ ...form, maritalStatus: e.target.value })}
                
              >
                <option value="">Select Status</option>
                <option value="single">Single</option>
                <option value="married">Married</option>
                <option value="separated">Separated</option>
                <option value="divorced">Divorced</option>
                <option value="single_parent">Single Parent</option>
                <option value="widowed">Widowed</option>
              </select>
            </div>
            </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-1">Date of Birth <span className="text-red-600">*</span></label>
              <div className="flex gap-2">
                {/* Month picker */}
                <select
                  className={`w-full p-3 border rounded ${
                    isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                  }`}
                  value={form.dobMonth}
                  onChange={e => setForm({ ...form, dobMonth: e.target.value })}
                >
                  <option value="">Month</option>
                  <option value="01">January</option>
                  <option value="02">February</option>
                  <option value="03">March</option>
                  <option value="04">April</option>
                  <option value="05">May</option>
                  <option value="06">June</option>
                  <option value="07">July</option>
                  <option value="08">August</option>
                  <option value="09">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </select>

                {/* Day picker */}
                <select
                  className={`w-24 p-3 border rounded ${
                    isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                  }`}
                  value={form.dobDay}
                  onChange={e => setForm({ ...form, dobDay: e.target.value })}
                  // required
                >
                  <option value="">Day</option>
                  {Array.from({ length: 31 }, (_, i) => (
                    <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                      {i + 1}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm mb-1">Age Group <span className="text-red-600">*</span></label>
              <select
                className={`w-full p-3 border rounded ${
                  isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                }`}
                value={form.ageGroup}
                onChange={e => setForm({ ...form, ageGroup: e.target.value })}
                // required
              >
                <option value="">Select Age Group</option>
                <option value="13-19">13-19</option>
                <option value="20-24">20-24</option>
                <option value="25-29">25-29</option>
                <option value="30-34">30-34</option>
                <option value="35-39">35-39</option>
                <option value="40-44">40-44</option>
                <option value="45-49">45-49</option>
                <option value="50-54">50-54</option>
                <option value="55-59">55-59</option>
                <option value="60+">60+</option>
              </select>
            </div>
            
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
              <label className="block text-sm mb-1">Occupation</label>
              <input
                className={`w-full p-3 border rounded ${
                  isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                }`}
                value={form.occupation}
                onChange={e => setForm({ ...form, occupation: e.target.value })}
                
              />
            </div>
            <div>
              <label className="block text-sm mb-1">Location <span className="text-red-600">*</span></label>
              <select
                className={`w-full p-3 border rounded ${
                  isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                }`}
                value={form.location_id}
                onChange={e => {
                  const val = e.target.value;
                  setForm({ ...form, location_id: val });
                  setShowOtherCity(val === 'other');
                }}
              >
                <option value="">Select a location</option>
                <option value="other">Other (specify city)</option>
                {locations.map(location => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
            {showOtherCity && (
              <div>
                <label className="block text-sm mb-1">Specify City</label>
                <input
                  className={`w-full p-3 border rounded ${
                    isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                  }`}
                  value={form.other_city}
                  onChange={e => setForm({ ...form, other_city: e.target.value })}
                  placeholder="Enter city name"
                  
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            <div>
              <label className="block text-sm mb-1">How did you hear about us? <span className="text-red-600">*</span></label>
              <select
                className={`w-full p-3 border rounded ${
                  isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                }`}
                value={form.howHeard}
                onChange={e => setForm({ ...form, howHeard: e.target.value })}
                // required
              >
                <option value="">Select Option</option>
                <option value="friend_family">Friend/Family</option>
                <option value="billboard">Billboard</option>
                <option value="flyer">Flyer</option>
                <option value="tv_radio">TV/Radio</option>
                <option value="social_media">Social Media</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1">Service Attended <span className="text-red-600">*</span></label>
              <select
                className={`w-full p-3 border rounded ${
                  isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                }`}
                value={form.serviceAttended}
                onChange={e => setForm({ ...form, serviceAttended: e.target.value })}
                required
              >
                <option value="first_service">Sunday First Service</option>
                <option value="second_service">Sunday Second Service</option>
                <option value="expression_church">Sunday Expression Church</option>
                <option value="midweek">Midweek Service</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
          </div>

          {visitType === 'first' ? (
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm mb-1">Would you consider attending RCCG - The Throne Room regularly? <span className="text-red-600">*</span></label>
                <div className="flex flex-wrap gap-4">
                  {['Yes','No','Maybe'].map(opt => (
                    <label key={opt} className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="consider_attending"
                        value={opt.toLowerCase()}
                        checked={form.considerAttending === opt.toLowerCase()}
                        onChange={() => setForm({ ...form, considerAttending: opt.toLowerCase() })}
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm mb-1">What were your three(3) favorite parts of the service? <span className="text-red-600">*</span></label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  {[
                    'Worship',
                    'In-church Media Experience',
                    'Sermon',
                    'Hall Ambience',
                    'First Timers Welcome',
                    'Church Friendliness',
                    'Online Experience (for virtual attendees)'
                  ].map(opt => (
                    <label key={opt} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={form.favoriteParts.includes(opt)}
                        onChange={e => {
                          const checked = e.target.checked;
                          let next = [...form.favoriteParts];
                          if (checked) {
                            next = Array.from(new Set([...next, opt])).slice(0, 7);
                          } else {
                            next = next.filter(x => x !== opt);
                          }
                          setForm({ ...form, favoriteParts: next });
                        }}
                        // required
                      />
                      {opt}
                    </label>
                  ))}
                </div>

                {form.favoriteParts.length < 3 && (
    <p className="text-xs text-red-500 mt-1">
      Please select at least 3 options.
    </p>
  )}

  <p className="text-xs text-gray-500 mt-1">
    You can select 3 or more.
  </p>
                {/* <p className="text-xs text-gray-500 mt-1">Select up to 3</p> */}
              </div>
              <div>
                <label className="block text-sm mb-1">Are you Visiting, Resident in or Relocating to Abuja? <span className="text-red-600">*</span></label>
                <div className="flex flex-wrap gap-4">
                  {['Visiting','Resident','Relocating'].map(opt => (
                    <label key={opt} className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="residency_status"
                        value={opt.toLowerCase()}
                        checked={form.residencyStatus === opt.toLowerCase()}
                        onChange={() => setForm({ ...form, residencyStatus: opt.toLowerCase() })}
                        //required
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm mb-1">Prayer requests</label>
                <textarea
                  rows={3}
                  className={`w-full p-3 border rounded ${
                    isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                  }`}
                  value={form.prayerRequests}
                  onChange={e => setForm({ ...form, prayerRequests: e.target.value })}
                  placeholder="Please share your prayer requests..."
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Feedback/Comments</label>
                <textarea
                  rows={3}
                  className={`w-full p-3 border rounded ${
                    isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                  }`}
                  value={form.feedback}
                  onChange={e => setForm({ ...form, feedback: e.target.value })}
                  placeholder="Please share your feedback about the service..."
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1">When was your first time?</label>
                <input
                  type="date"
                  className={`w-full p-3 border rounded ${
                    isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                  }`}
                  value={form.firstVisitDate}
                  onChange={e => setForm({ ...form, firstVisitDate: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm mb-1">Did you receive a call, text or visit the first time you joined us? <span className="text-red-600">*</span></label>
                <select
                  className={`w-full p-3 border rounded ${
                    isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                  }`}
                  value={form.receivedFollowUp}
                  onChange={e => setForm({ ...form, receivedFollowUp: e.target.value })}
                  //required
                >
                  <option value="">Select Option</option>
                  <option value="call">Call</option>
                  <option value="text">Text</option>
                  <option value="visit">Visit</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Have you been baptized by immersion? <span className="text-red-600">*</span></label>
                <select
                  className={`w-full p-3 border rounded ${
                    isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                  }`}
                  value={form.baptized}
                  onChange={e => setForm({ ...form, baptized: e.target.value })}
                  //required
                >
                  <option value="">Select Option</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Would you like to be baptized? <span className="text-red-600">*</span></label>
                <select
                  className={`w-full p-3 border rounded ${
                    isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                  }`}
                  value={form.wouldLikeBaptism}
                  onChange={e => setForm({ ...form, wouldLikeBaptism: e.target.value })}
                  //required
                >
                  <option value="">Select Option</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Would you like to join the workforce?</label>
                <select
                  className={`w-full p-3 border rounded ${
                    isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                  }`}
                  value={form.workforce}
                  onChange={e => setForm({ ...form, workforce: e.target.value })}
                >
                  <option value="">Select Option</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Preferred department</label>
                <select
                  className={`w-full p-3 border rounded ${
                    isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                  }`}
                  value={form.department}
                  onChange={e => setForm({ ...form, department: e.target.value })}
                >
                  <option value="">Select Department</option>
                  <option value="throne_room_priests">Throne Room Priests (Choir)</option>
                  <option value="ushering">Ushering</option>
                  <option value="protocol">Protocol</option>
                  <option value="technical">Technical</option>
                  <option value="junior_church">Junior Church</option>
                  <option value="counseling">Counseling</option>
                  <option value="sunday_school">Sunday School</option>
                  <option value="follow_up">Follow Up</option>
                  <option value="prayer">Prayer</option>
                  <option value="evangelism">Evangelism</option>
                  <option value="media">Media</option>
                  <option value="theatre_company">Throne Room Theatre Company(Drama)</option>
                  <option value="welfare">Welfare</option>
                  <option value="sanitation">Sanitation</option>
                  <option value="inetwork">iNetwork</option>
                  
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Preferred time of day for visit <span className="text-red-600">*</span></label>
                <select
                  className={`w-full p-3 border rounded ${
                    isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                  }`}
                  value={form.preferredVisitTime}
                  onChange={e => setForm({ ...form, preferredVisitTime: e.target.value })}
                  // required
                >
                  <option value="">Select Time</option>
                  <option value="morning">Morning</option>
                  <option value="afternoon">Afternoon</option>
                  <option value="evening">Evening</option>
                </select>
              </div>
              <div>
                <label className="block text-sm mb-1">Preferred day of week for visit <span className="text-red-600">*</span></label>
                <select
                  className={`w-full p-3 border rounded ${
                    isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                  }`}
                  value={form.preferredVisitDay}
                  onChange={e => setForm({ ...form, preferredVisitDay: e.target.value })}
                  // required
                >
                  <option value="">Select Day</option>
                  <option value="sunday">Sunday</option>
                  <option value="monday">Monday</option>
                  <option value="tuesday">Tuesday</option>
                  <option value="wednesday">Wednesday</option>
                  <option value="thursday">Thursday</option>
                  <option value="friday">Friday</option>
                  <option value="saturday">Saturday</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm mb-1">
                  If you are already a member of RCCG before coming, which parish?
                </label>
                <input
                  className={`w-full p-3 border rounded ${
                    isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                  }`}
                  value={form.previousParish}
                  onChange={e => setForm({ ...form, previousParish: e.target.value })}
                  placeholder="Parish name and Location"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm mb-1">Visit notes</label>
                <textarea
                  rows={3}
                  className={`w-full p-3 border rounded ${
                    isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : ''
                  }`}
                  value={form.visitNotes}
                  onChange={e => setForm({ ...form, visitNotes: e.target.value })}
                  placeholder="Additional notes for follow-up..."
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 rounded-xl font-semibold text-white disabled:opacity-50 ${
              visitType === 'first' ? 'bg-green-600' : 'bg-amber-900'
            }`}
          >
            {loading ? 'Saving...' : 'Submit'}
          </button>
        </form>
        )}

        {visitType === 'newbirth' && (
          <NewBirthForm API_BASE_URL={API_BASE_URL} isDark={isDark} />
        )}
      </div>
    </div>
  );
};

export default VisitorExtra;


